import React from 'react';
import { useTranslation } from 'react-i18next';
import images from '../../data/images.json';
import './Manjushree.css';

const Manjushree: React.FC = () => {
  const { t } = useTranslation();
  const [lightboxImage, setLightboxImage] = React.useState<string | null>(null);

  const openLightbox = (imageSrc: string) => {
    setLightboxImage(imageSrc);
  };

  const closeLightbox = () => {
    setLightboxImage(null);
  };

  return (
    <div className="manjushree-page">
      {/* Lightbox Modal */}
      {lightboxImage && (
        <div className="lightbox-overlay" onClick={closeLightbox}>
          <div className="lightbox-content">
            <button className="lightbox-close" onClick={closeLightbox}>×</button>
            <img src={lightboxImage} alt="Fullscreen view" className="lightbox-image" />
          </div>
        </div>
      )}
      <div className="manjushree-hero">
        <div className="container">
          <h1 className="page-title">{t('manjushree.title')}</h1>
          <p className="page-subtitle">{t('manjushree.subtitle')}</p>
        </div>
      </div>

      <section className="manjushree-section">
        <div className="container">
          <div className="manjushree-content">
            {/* Introduction */}
            <div className="story-section">
              <div className="story-image-wrapper">
                <img
                  src={images.manjushree?.main || '/images/manjushree/manjushree-main.jpg'}
                  alt={t('manjushree.introduction.imageAlt')}
                  className="story-image"
                  onClick={() => openLightbox(images.manjushree?.main || '/images/manjushree/manjushree-main.jpg')}
                  style={{ cursor: 'pointer' }}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = '/images/hero/slider-1.jpg';
                  }}
                />
              </div>
              <div className="story-text">
                <h2>{t('manjushree.introduction.title')}</h2>
                <p>{t('manjushree.introduction.paragraph1')}</p>
                <p>{t('manjushree.introduction.paragraph2')}</p>
              </div>
            </div>

            {/* Origin Story */}
            <div className="story-section reverse">
              <div className="story-text">
                <h2>{t('manjushree.origin.title')}</h2>
                <p>{t('manjushree.origin.paragraph1')}</p>
                <p>{t('manjushree.origin.paragraph2')}</p>
                <p>{t('manjushree.origin.paragraph3')}</p>
              </div>
              <div className="story-image-wrapper">
                <img
                  src={images.manjushree?.origin || '/images/manjushree/manjushree-origin.jpg'}
                  alt={t('manjushree.origin.imageAlt')}
                  className="story-image"
                  onClick={() => openLightbox(images.manjushree?.origin || '/images/manjushree/manjushree-origin.jpg')}
                  style={{ cursor: 'pointer' }}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = '/images/hero/slider-2.jpg';
                  }}
                />
              </div>
            </div>

            {/* Symbolism */}
            <div className="story-section">
              <div className="story-text full-width">
                <h2>{t('manjushree.symbolism.title')}</h2>
                <div className="symbolism-items">
                  <div className="symbolism-item with-image">
                    <div className="symbolism-image-wrapper">
                      <img
                        src={images.manjushree?.sword || '/images/manjushree/sword.jpg'}
                        alt={t('manjushree.symbolism.sword.title')}
                        className="symbolism-image"
                        onClick={() => openLightbox(images.manjushree?.sword || '/images/manjushree/sword.jpg')}
                        style={{ cursor: 'pointer' }}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = '/images/causes/case-img-1.jpg';
                        }}
                      />
                    </div>
                    <div className="symbolism-text">
                      <h3>{t('manjushree.symbolism.sword.title')}</h3>
                      <p>{t('manjushree.symbolism.sword.description')}</p>
                    </div>
                  </div>
                  <div className="symbolism-item with-image">
                    <div className="symbolism-image-wrapper">
                      <img
                        src={images.manjushree?.scripture || '/images/manjushree/scripture.jpg'}
                        alt={t('manjushree.symbolism.book.title')}
                        className="symbolism-image"
                        onClick={() => openLightbox(images.manjushree?.scripture || '/images/manjushree/scripture.jpg')}
                        style={{ cursor: 'pointer' }}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = '/images/causes/case-img-2.jpg';
                        }}
                      />
                    </div>
                    <div className="symbolism-text">
                      <h3>{t('manjushree.symbolism.book.title')}</h3>
                      <p>{t('manjushree.symbolism.book.description')}</p>
                    </div>
                  </div>
                  <div className="symbolism-item with-image">
                    <div className="symbolism-image-wrapper">
                      <img
                        src={images.manjushree?.lotus || '/images/manjushree/lotus.jpg'}
                        alt={t('manjushree.symbolism.lotus.title')}
                        className="symbolism-image"
                        onClick={() => openLightbox(images.manjushree?.lotus || '/images/manjushree/lotus.jpg')}
                        style={{ cursor: 'pointer' }}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = '/images/causes/case-img-3.jpg';
                        }}
                      />
                    </div>
                    <div className="symbolism-text">
                      <h3>{t('manjushree.symbolism.lotus.title')}</h3>
                      <p>{t('manjushree.symbolism.lotus.description')}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Teachings */}
            <div className="story-section teachings-section">
              <div className="story-text full-width">
                <h2>{t('manjushree.teachings.title')}</h2>
                <p>{t('manjushree.teachings.paragraph1')}</p>
                <ul className="teachings-list">
                  <li>{t('manjushree.teachings.teaching1')}</li>
                  <li>{t('manjushree.teachings.teaching2')}</li>
                  <li>{t('manjushree.teachings.teaching3')}</li>
                  <li>{t('manjushree.teachings.teaching4')}</li>
                  <li>{t('manjushree.teachings.teaching5')}</li>
                  <li>{t('manjushree.teachings.teaching6')}</li>
                  <li>{t('manjushree.teachings.teaching7')}</li>
                  <li>{t('manjushree.teachings.teaching8')}</li>
                  <li>{t('manjushree.teachings.teaching9')}</li>
                  <li>{t('manjushree.teachings.teaching10')}</li>
                  <li>{t('manjushree.teachings.teaching11')}</li>
                  <li>{t('manjushree.teachings.teaching12')}</li>
                  <li>{t('manjushree.teachings.teaching13')}</li>
                  <li>{t('manjushree.teachings.teaching14')}</li>
                  <li>{t('manjushree.teachings.teaching15')}</li>
                  <li>{t('manjushree.teachings.teaching16')}</li>
                  <li>{t('manjushree.teachings.teaching17')}</li>
                  <li>{t('manjushree.teachings.teaching18')}</li>
                  <li>{t('manjushree.teachings.teaching19')}</li>
                </ul>
                <p>{t('manjushree.teachings.paragraph2')}</p>
              </div>

              {/* Image Gallery */}
              <div className="teachings-gallery">
                {Array.isArray(images.manjushree?.teachings) && images.manjushree.teachings.map((imageSrc, index) => (
                  <div
                    key={index}
                    className="gallery-image-item"
                    onClick={() => openLightbox(imageSrc)}
                    style={{ cursor: 'pointer' }}
                  >
                    <img
                      src={imageSrc}
                      alt={`Buddhist Teaching Art ${index + 1}`}
                      className="gallery-image"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = '/images/hero/slider-1.jpg';
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Importance */}
            <div className="story-section">
              <div className="story-image-wrapper">
                <img
                  src={images.manjushree?.childrenStudying || '/images/manjushree/children_studying.jpg'}
                  alt={t('manjushree.importance.imageAlt')}
                  className="story-image"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = '/images/causes/case-img-3.jpg';
                  }}
                />
              </div>
              <div className="story-text">
                <h2>{t('manjushree.importance.title')}</h2>
                <p>{t('manjushree.importance.paragraph1')}</p>
                <p>{t('manjushree.importance.paragraph2')}</p>
                <p>{t('manjushree.importance.paragraph3')}</p>
              </div>
            </div>

            {/* Connection to Foundation */}
            <div className="foundation-connection">
              <div className="container">
                <h2>{t('manjushree.foundation.title')}</h2>
                <div className="connection-content">
                  <div className="connection-image-wrapper">
                    <img
                      src={images.manjushree?.foundation || '/images/manjushree/manjushree-foundation.jpg'}
                      alt={t('manjushree.foundation.imageAlt')}
                      className="connection-image"
                      onClick={() => openLightbox(images.manjushree?.foundation || '/images/manjushree/manjushree-foundation.jpg')}
                      style={{ cursor: 'pointer' }}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = images.logo || '/images/logo.png';
                      }}
                    />
                  </div>
                  <div className="connection-text">
                    <p>{t('manjushree.foundation.paragraph1')}</p>
                    <p>{t('manjushree.foundation.paragraph2')}</p>
                    <p>{t('manjushree.foundation.paragraph3')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Manjushree;


